import { createSlice } from '@reduxjs/toolkit';
import { calculateProductPricing, applyWasteRate } from '../../Services/quoteCalculator';

// Charger l'état initial depuis localStorage
const loadStateFromStorage = () => {
  try {
    const serializedState = localStorage.getItem('selectedProducts');
    if (serializedState === null) {
      return {
        selectedProducts: [], // Array of selected products with quantities
        selectedChoices: [], // Array of selected choice items
        totalPrice: 0,
        userInputs: {}, // Store user inputs from different sections
        projectType: null, // Type de projet pour les calculs
        surfaceArea: 0, // Surface en m² pour les calculs
        sectionNames: {} // Store section names by ID
      };
    }
    const parsedState = JSON.parse(serializedState);
    return {
      selectedProducts: parsedState.selectedProducts || [],
      selectedChoices: parsedState.selectedChoices || [],
      totalPrice: parsedState.totalPrice || 0,
      userInputs: parsedState.userInputs || {},
      projectType: parsedState.projectType || null,
      surfaceArea: parsedState.surfaceArea || 0,
      sectionNames: parsedState.sectionNames || {}
    };
  } catch (err) {
    console.error('Erreur lors du chargement depuis localStorage:', err);
    return {
      selectedProducts: [], // Array of selected products with quantities
      selectedChoices: [], // Array of selected choice items
      totalPrice: 0,
      userInputs: {}, // Store user inputs from different sections
      projectType: null, // Type de projet pour les calculs
      surfaceArea: 0, // Surface en m² pour les calculs
      sectionNames: {} // Store section names by ID
    };
  }
};

const initialState = loadStateFromStorage();

// Fonction pour sauvegarder dans localStorage
const saveStateToStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('selectedProducts', serializedState);
  } catch (err) {
    console.error('Erreur lors de la sauvegarde dans localStorage:', err);
  }
};

const selectedProductsSlice = createSlice({
  name: 'selectedProducts',
  initialState,
  reducers: {
    addProduct: (state, action) => {
      const { product, quantity = 1, section, productType, contentProductId } = action.payload;
      const existingProduct = state.selectedProducts.find(
        p => p.contentProductId === contentProductId && p.section === section
      );
      
      // Calculer la quantité automatiquement si c'est un nouveau produit
      const calculatedPricing = calculateProductPricing(
        product, 
        productType, 
        state.projectType, 
        state.surfaceArea, 
        state.userInputs,
        state.selectedChoices
      );
      
      const finalQuantity = existingProduct ? existingProduct.quantity + quantity : calculatedPricing.quantity;
      const finalPrice = product.price * finalQuantity;
      
      if (existingProduct) {
        existingProduct.quantity = finalQuantity;
        existingProduct.totalPrice = finalPrice;
      } else {
        state.selectedProducts.push({
          ...product,
          quantity: finalQuantity,
          section,
          productType,
          contentProductId,
          totalPrice: finalPrice,
          calculatedQuantity: calculatedPricing.quantity,
          isCalculated: true
        });
      }
      
      state.totalPrice = state.selectedProducts.reduce(
        (total, product) => total + product.totalPrice, 0
      );
      
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    removeProduct: (state, action) => {
      const { contentProductId, section } = action.payload;
      state.selectedProducts = state.selectedProducts.filter(
        p => !(p.contentProductId === contentProductId && p.section === section)
      );
      
      state.totalPrice = state.selectedProducts.reduce(
        (total, product) => total + product.totalPrice, 0
      );
      
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    updateProductQuantity: (state, action) => {
      const { contentProductId, section, quantity } = action.payload;
      const product = state.selectedProducts.find(
        p => p.contentProductId === contentProductId && p.section === section
      );
      
      if (product) {
        product.quantity = quantity;
        product.totalPrice = product.price * quantity;
        product.isCalculated = false; // Marquer comme modifié manuellement
        
        state.totalPrice = state.selectedProducts.reduce(
          (total, product) => total + product.totalPrice, 0
        );
      }
      
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    setUserInput: (state, action) => {
      const { section, input } = action.payload;
      state.userInputs[section] = input;
      
      // Recalculer les quantités si la surface a changé
      if (Object.keys(input).some(key => 
        key.toLowerCase().includes('surface') || 
        key.toLowerCase().includes('m²') || 
        key.toLowerCase().includes('m2')
      )) {
        // Recalculer tous les produits calculés automatiquement
        state.selectedProducts.forEach(product => {
          if (product.isCalculated) {
            const calculatedPricing = calculateProductPricing(
              product, 
              product.productType, 
              state.projectType, 
              state.surfaceArea, 
              state.userInputs,
              state.selectedChoices
            );
            product.quantity = calculatedPricing.quantity;
            product.totalPrice = product.price * calculatedPricing.quantity;
          }
        });
        
        state.totalPrice = state.selectedProducts.reduce(
          (total, product) => total + product.totalPrice, 0
        );
      }
      
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    setProjectType: (state, action) => {
      state.projectType = action.payload;
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    setSurfaceArea: (state, action) => {
      state.surfaceArea = action.payload;
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    setSectionName: (state, action) => {
      const { sectionId, sectionName } = action.payload;
      state.sectionNames[sectionId] = sectionName;
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    setSectionNames: (state, action) => {
      state.sectionNames = action.payload;
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    recalculateAllProducts: (state) => {
      // Recalculer tous les produits avec les nouvelles données
      state.selectedProducts.forEach(product => {
        if (product.isCalculated) {
          const calculatedPricing = calculateProductPricing(
            product, 
            product.productType, 
            state.projectType, 
            state.surfaceArea, 
            state.userInputs,
            state.selectedChoices
          );
          product.quantity = calculatedPricing.quantity;
          product.totalPrice = product.price * calculatedPricing.quantity;
        }
      });
      
      state.totalPrice = state.selectedProducts.reduce(
        (total, product) => total + product.totalPrice, 0
      );
      
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    },
    
    toggleChoiceItem: (state, action) => {
      const { choice, sectionId } = action.payload;
      const existingIndex = state.selectedChoices.findIndex(
        selected => selected.id === choice.id
      );

      if (existingIndex >= 0) {
        state.selectedChoices.splice(existingIndex, 1);
      } else {
        state.selectedChoices.push({
          ...choice,
          sectionId
        });
      }

      // Recalculer les produits si c'est un projet BARDAGE et que le choix concerne l'orientation
      // ou si c'est un autre type de projet qui dépend des choix
      if (state.projectType === 'BARDAGE' && 
          (choice.code === 'SENS_POSE_HORIZONTALE' || choice.code === 'SENS_POSE_VERTICALE')) {
        // Recalculer tous les produits calculés automatiquement
        state.selectedProducts.forEach(product => {
          if (product.isCalculated) {
            const calculatedPricing = calculateProductPricing(
              product, 
              product.productType, 
              state.projectType, 
              state.surfaceArea, 
              state.userInputs,
              state.selectedChoices
            );
            product.quantity = calculatedPricing.quantity;
            product.totalPrice = product.price * calculatedPricing.quantity;
          }
        });
        
        state.totalPrice = state.selectedProducts.reduce(
          (total, product) => total + product.totalPrice, 0
        );
      }

      saveStateToStorage(state);
    },

    clearSelectedProducts: (state) => {
      state.selectedProducts = [];
      state.selectedChoices = [];
      state.totalPrice = 0;
      state.userInputs = {};
      // Sauvegarder dans localStorage
      saveStateToStorage(state);
    }
  }
});

export const { 
  addProduct, 
  removeProduct, 
  updateProductQuantity, 
  setUserInput, 
  setProjectType,
  setSurfaceArea,
  setSectionName,
  setSectionNames,
  recalculateAllProducts,
  toggleChoiceItem,
  clearSelectedProducts 
} = selectedProductsSlice.actions;

export default selectedProductsSlice.reducer;
